import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCountUp } from '@/hooks/useCountUp';
import { useLiveData } from '@/hooks/useLiveData';

/* ── Animated Metric Cell ── */
function MetricCell({ value, label, type, target, delay }: {
  value: string;
  label: string;
  type: 'count' | 'count-plus' | 'alpha' | 'text';
  target: number;
  delay: number;
}) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const { formatted } = useCountUp({ target, duration: 1800, delay, enabled: visible });

  const displayValue = type === 'text' ? value
    : type === 'alpha' ? `α ${visible ? target.toFixed(1) : '0.0'}`
    : type === 'count-plus' ? `${formatted}+`
    : formatted;

  return (
    <div ref={ref} className="px-2 py-2.5 text-center">
      <div className={`mono-data text-sm md:text-base font-bold text-foreground-100 leading-none mb-0.5 transition-all duration-300 ${visible ? 'opacity-100' : 'opacity-0 translate-y-1'}`}
        style={{ transitionDelay: `${delay + 200}ms` }}>
        {displayValue}
      </div>
      <div className="text-[9px] text-foreground-600 font-medium leading-tight">{label}</div>
    </div>
  );
}

/* ── Live Pulse Dot ── */
function LiveDot({ color = 'emerald' }: { color?: string }) {
  const colorMap = { emerald: 'bg-emerald-400', gold: 'bg-accent-400', blue: 'bg-primary-400' };
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${colorMap[color]} animate-pulse-glow`}></span>;
}

/* ── Live Mini Sparkline ── */
function MiniSparkline({ height = 32, color = '#48D1B0', seed = 0 }: { height?: number; color?: string; seed?: number }) {
  const points = useRef<number[]>(
    Array.from({ length: 24 }, (_, i) => 30 + Math.sin(i * 0.8 + seed) * 20 + Math.cos(i * 1.3 + seed) * 12 + (Math.random() - 0.5) * 8)
  ).current;

  const maxVal = Math.max(...points);
  const w = 200, h = height;
  const pathD = points.map((v, i) => {
    const x = (i / (points.length - 1)) * w;
    const y = h - (v / maxVal) * (h - 2);
    return `${i === 0 ? 'M' : 'L'}${x} ${y}`;
  }).join(' ');

  const fillD = `${pathD} L${w} ${h} L0 ${h} Z`;

  return (
    <div className="h-8 w-full overflow-hidden opacity-70 group-hover:opacity-100 transition-opacity duration-500">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={`spark-mini-${seed}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.18" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={fillD} fill={`url(#spark-mini-${seed})`} />
        <path d={pathD} fill="none" stroke={color} strokeWidth="1.2" strokeOpacity="0.7" className="sparkline-path" />
      </svg>
    </div>
  );
}

const businesses = [
  {
    title: '蚂蚁量化',
    subtitle: 'AI 驱动的量化研究与策略管理',
    description: '依托深度学习与大数据分析，构建多因子量化模型，实现全流程智能风控与策略优化。',
    icon: 'ri-line-chart-line',
    metrics: [
      { value: '50+', label: '策略模型', type: 'count-plus' as const, target: 50 },
      { value: '12', label: '风控维度', type: 'count' as const, target: 12 },
      { value: 'α 2.1', label: '超额基准', type: 'alpha' as const, target: 2.1 },
    ],
    liveMetric: { label: '今日信号', initial: 127, range: 15 },
    highlight: '多因子 / CTA / 统计套利 / 波动率曲面',
    image: 'https://readdy.ai/api/search-image?query=Abstract%20quantitative%20trading%20algorithmic%20data%20streams%2C%20glowing%20blue%20and%20teal%20mathematical%20formulas%20floating%20in%20dark%20space%2C%20futuristic%20fintech%20aesthetic%2C%20neon%20data%20lines%20and%20candlestick%20silhouettes%2C%20deep%20navy%20background%2C%20no%20text%2C%20professional%20dark%20tone%2C%20motion%20blur%20data%20flow&width=800&height=560&seq=biz-quant-terminal-01&orientation=landscape',
    link: '/quant',
    sparkColor: '#48D1B0',
    dotColor: 'emerald' as const,
  },
  {
    title: '蚂蚁基金',
    subtitle: '基金研究与资产配置管理',
    description: '深度研究全球基金市场，基于宏观周期与风险预算模型，为机构与高净值客户提供跨资产类别的定制化资产管理方案。',
    icon: 'ri-funds-line',
    metrics: [
      { value: '200+', label: '覆盖基金', type: 'count-plus' as const, target: 200 },
      { value: '8', label: '资产类别', type: 'count' as const, target: 8 },
      { value: '12', label: '全球市场', type: 'count' as const, target: 12 },
    ],
    liveMetric: { label: '组合收益率', initial: 8.4, range: 0.3 },
    highlight: '多维度评价 / 组合优化 / 风格因子归因',
    image: 'https://readdy.ai/api/search-image?query=Professional%20fund%20portfolio%20allocation%20abstract%20visualization%2C%20glowing%20geometric%20pie%20chart%20rings%20and%20asset%20distribution%20nodes%2C%20dark%20navy%20background%2C%20golden%20accent%20connection%20lines%2C%20sophisticated%20clean%20corporate%20fintech%20style%2C%20warm%20gold%20and%20cool%20blue%20contrast%2C%20no%20text&width=800&height=560&seq=biz-fund-terminal-02&orientation=landscape',
    link: '/business',
    sparkColor: '#C8A840',
    dotColor: 'gold' as const,
  },
  {
    title: '蚂蚁家办',
    subtitle: '家族资产管理 · 财富永续传承',
    description: '为超高净值家族提供一站式资产管理服务，涵盖全球资产配置、家族信托架构、税务筹划与代际传承方案。',
    icon: 'ri-building-2-line',
    metrics: [
      { value: '50+', label: '服务家族', type: 'count-plus' as const, target: 50 },
      { value: '6', label: '跨境架构', type: 'count' as const, target: 6 },
      { value: '定制', label: '传承方案', type: 'text' as const, target: 0 },
    ],
    liveMetric: { label: '在管资产', initial: 1.2, range: 0.02 },
    highlight: '家族信托 / 税务筹划 / 代际传承 / 全球合规',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20family%20office%20wealth%20legacy%20abstract%20visualization%2C%20dark%20navy%20background%2C%20interconnected%20golden%20nodes%20forming%20heritage%20tree%20structure%2C%20warm%20luxury%20atmosphere%2C%20sophisticated%20trustworthy%20aesthetic%2C%20subtle%20classical%20elements%20merged%20with%20modern%20fintech%2C%20no%20text&width=800&height=560&seq=biz-family-terminal-03&orientation=landscape',
    link: '/business',
    sparkColor: '#C8A840',
    dotColor: 'gold' as const,
  },
  {
    title: '蚂蚁资本',
    subtitle: '私募股权投资 · 并购资产管理',
    description: '聚焦新兴科技赛道，提供从种子轮到Pre-IPO的全阶段股权投资，结合并购顾问与投后管理，实现资产长期增值。',
    icon: 'ri-rocket-line',
    metrics: [
      { value: '30+', label: '投后项目', type: 'count-plus' as const, target: 30 },
      { value: '15+', label: '退出案例', type: 'count-plus' as const, target: 15 },
      { value: '100+', label: '专业团队', type: 'count-plus' as const, target: 100 },
    ],
    liveMetric: { label: '已投规模', initial: 860, range: 20 },
    highlight: '股权投资 / 并购咨询 / 战略孵化 / 跨境交易',
    image: 'https://readdy.ai/api/search-image?query=Abstract%20venture%20capital%20and%20innovation%20ecosystem%2C%20dark%20navy%20background%2C%20interconnected%20rising%20geometric%20nodes%20with%20teal%20and%20gold%20lines%2C%20growth%20trajectory%20indicators%2C%20modern%20fintech%20investment%20aesthetic%2C%20clean%20dark%20professional%20style%2C%20no%20text&width=800&height=560&seq=biz-capital-terminal-04&orientation=landscape',
    link: '/business',
    sparkColor: '#60A8E0',
    dotColor: 'blue' as const,
  },
];

/* ── Live Metric Display ── */
function LiveMetricBadge({ label, initial, range, colorClass = 'text-emerald-400' }: {
  label: string;
  initial: number;
  range: number;
  colorClass?: string;
}) {
  const { formatted, flash } = useLiveData({ initial, range, decimals: 1, interval: 2500 });
  return (
    <div className="flex items-center gap-1.5">
      <LiveDot color={colorClass.includes('emerald') ? 'emerald' : colorClass.includes('accent') ? 'gold' : 'blue'} />
      <span className="text-[9px] text-foreground-600 mono-data">{label}</span>
      <span className={`text-[10px] mono-data font-bold transition-all duration-300 ${flash ? 'data-tick' : ''} ${colorClass}`}>
        {formatted}
      </span>
    </div>
  );
}

export default function BusinessCards() {
  return (
    <section className="relative py-24 md:py-32 bg-background-50 overflow-hidden">
      <div className="absolute inset-0 bg-dot-matrix opacity-40"></div>
      <div className="absolute inset-0 bg-fintech-grid-sm opacity-20"></div>

      <div className="absolute left-0 top-[5%] bottom-[5%] w-px bg-gradient-to-b from-transparent via-primary-400/10 to-transparent"></div>
      <div className="absolute right-0 top-[5%] bottom-[5%] w-px bg-gradient-to-b from-transparent via-accent-400/8 to-transparent"></div>

      <div className="relative px-6 md:px-12 lg:px-20">
        <div className="max-w-[1400px] mx-auto">

          {/* Section header */}
          <div className="text-center mb-16 md:mb-20">
            <div className="inline-flex items-center gap-2 mb-4">
              <LiveDot color="emerald" />
              <span className="mono-data text-[10px] text-foreground-600 tracking-[0.2em] uppercase">Core Capabilities</span>
              <LiveDot color="blue" />
            </div>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-100 leading-tight">
              四大核心<span className="text-primary-400">业务板块</span>
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              以量化科技驱动资产管理，构建量化研究、基金管理、家族办公室与资本投资的协同生态
            </p>
          </div>

          {/* Business Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-5">
            {businesses.map((biz, idx) => (
              <Link
                to={biz.link}
                key={biz.title}
                data-product-shop
                className="group terminal-module overflow-hidden transition-all duration-500 hover:-translate-y-1.5 hover:border-accent-500/40 animate-fade-in-up"
                style={{ animationDelay: `${0.1 * (idx + 1)}s` }}
              >
                {/* Image area */}
                <div className="relative h-44 md:h-48 overflow-hidden">
                  <img
                    src={biz.image}
                    alt={biz.title}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-200/90 via-background-200/30 to-transparent"></div>

                  {/* Live dot + status */}
                  <div className="absolute top-3 left-3 flex items-center gap-1.5">
                    <LiveDot color={biz.dotColor} />
                    <span className="text-[8px] mono-data text-foreground-500 tracking-wider">LIVE</span>
                  </div>

                  {/* Icon badge - top right */}
                  <div className="absolute top-3 right-3 w-9 h-9 rounded-lg bg-background-100/70 backdrop-blur-md border border-background-300/30 flex items-center justify-center group-hover:border-accent-500/40 transition-colors duration-300">
                    <i className={`${biz.icon} text-base text-primary-400 group-hover:text-accent-400 transition-colors duration-300`}></i>
                  </div>

                  {/* Title overlay on image */}
                  <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                    <h3 className="text-lg md:text-xl font-heading font-bold text-white group-hover:text-accent-400 transition-colors duration-300">
                      {biz.title}
                    </h3>
                    <LiveMetricBadge
                      label={biz.liveMetric.label}
                      initial={biz.liveMetric.initial}
                      range={biz.liveMetric.range}
                      colorClass={biz.sparkColor === '#48D1B0' ? 'text-emerald-400' : biz.sparkColor === '#C8A840' ? 'text-accent-400' : 'text-primary-400'}
                    />
                  </div>
                </div>

                {/* Metrics row with animated counters */}
                <div className="grid grid-cols-3 divide-x divide-background-300/30 border-b border-background-300/30">
                  {biz.metrics.map((m, mi) => (
                    <MetricCell
                      key={mi}
                      value={m.value}
                      label={m.label}
                      type={m.type}
                      target={m.target}
                      delay={100 + mi * 150}
                    />
                  ))}
                </div>

                {/* Mini live sparkline */}
                <MiniSparkline height={32} color={biz.sparkColor} seed={idx * 7} />

                {/* Content body */}
                <div className="p-4 md:p-5">
                  <p className="text-[11px] md:text-xs text-primary-400 font-medium mb-2 tracking-wide">{biz.subtitle}</p>
                  <p className="text-xs md:text-[13px] text-foreground-500 leading-relaxed mb-3">{biz.description}</p>

                  {/* Capability tags */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {biz.highlight.split(' / ').map((tag) => (
                      <span key={tag} className="px-2 py-0.5 text-[9px] text-foreground-500 bg-background-200/60 border border-background-300/30 rounded-sm transition-colors duration-300 group-hover:border-accent-500/20 group-hover:text-foreground-400">
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* CTA line with animated arrow */}
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-accent-500 group-hover:text-accent-400 transition-colors duration-200 whitespace-nowrap">
                    <span>了解更多</span>
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200 text-[10px]"></i>
                  </div>
                </div>

                {/* Bottom gold expand line on hover */}
                <div className="mx-4 h-px bg-gradient-to-r from-transparent via-accent-500/30 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left"></div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}